import { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@apollo/client/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { LIST_CLIENTS, GET_INQUIRIES } from '../../graphql/queries';

// Types
export type ParticipantType = 'CLIENT' | 'INQUIRY' | 'GUEST';

export interface Participant {
  id: string;
  type: ParticipantType;
  referenceId?: string;
  name: string;
  email?: string;
  phone?: string;
  notifyEmail: boolean;
  notifyPush: boolean;
  notifySms: boolean;
}

interface Client {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
}

interface Inquiry {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phoneNumber?: string;
  userId?: string;
}

interface ListClientsData {
  listClients: Client[];
}

interface ListInquiriesData {
  listInquiries: {
    items: Inquiry[];
  };
}

type AddMode = 'none' | 'client' | 'inquiry' | 'guest';

interface GuestFormData {
  name: string;
  email: string;
  phone: string;
}

interface ParticipantSelectorProps {
  selectedParticipants: Participant[];
  onParticipantsChange: (participants: Participant[]) => void;
  maxParticipants?: number;
}

// Helper to generate unique ID
const generateId = () => `participant-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

// Type badge colors
const typeBadgeColors: Record<ParticipantType, { bg: string; text: string }> = {
  CLIENT: { bg: 'bg-blue-100', text: 'text-blue-700' },
  INQUIRY: { bg: 'bg-purple-100', text: 'text-purple-700' },
  GUEST: { bg: 'bg-gray-100', text: 'text-gray-700' },
};

// Type icons
const typeIcons: Record<ParticipantType, string> = {
  CLIENT: '👤',
  INQUIRY: '📋',
  GUEST: '🔗',
};

export function ParticipantSelector({
  selectedParticipants,
  onParticipantsChange,
  maxParticipants = 10,
}: ParticipantSelectorProps) {
  const [addMode, setAddMode] = useState<AddMode>('none');
  const [searchTerm, setSearchTerm] = useState('');
  const [guestForm, setGuestForm] = useState<GuestFormData>({ name: '', email: '', phone: '' });

  // Queries
  const { data: clientsData, loading: clientsLoading } = useQuery<ListClientsData>(LIST_CLIENTS, {
    skip: addMode !== 'client',
  });

  const { data: inquiriesData, loading: inquiriesLoading } = useQuery<ListInquiriesData>(GET_INQUIRIES, {
    variables: { limit: 200 },
    skip: addMode !== 'inquiry',
  });

  const clients: Client[] = clientsData?.listClients || [];
  const inquiries: Inquiry[] = inquiriesData?.listInquiries?.items || [];

  // Filter clients based on search term and exclude already selected
  const filteredClients = useMemo(() => {
    const selectedClientIds = selectedParticipants
      .filter((p) => p.type === 'CLIENT')
      .map((p) => p.referenceId);

    let filtered = clients.filter((c) => !selectedClientIds.includes(c.id));

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter((client) => {
        const fullName = `${client.firstName} ${client.lastName}`.toLowerCase();
        const email = client.email?.toLowerCase() || '';
        return fullName.includes(query) || email.includes(query);
      });
    }

    return filtered;
  }, [clients, searchTerm, selectedParticipants]);

  // Filter inquiries based on search term and exclude already selected
  const filteredInquiries = useMemo(() => {
    const selectedInquiryIds = selectedParticipants
      .filter((p) => p.type === 'INQUIRY')
      .map((p) => p.referenceId);

    let filtered = inquiries.filter((i) => !selectedInquiryIds.includes(i.id));

    if (searchTerm.trim()) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter((inquiry) => {
        const fullName = `${inquiry.firstName} ${inquiry.lastName}`.toLowerCase();
        const email = inquiry.email?.toLowerCase() || '';
        return fullName.includes(query) || email.includes(query);
      });
    }

    return filtered;
  }, [inquiries, searchTerm, selectedParticipants]);

  // Handlers
  const handleAddClient = useCallback(
    (client: Client) => {
      const newParticipant: Participant = {
        id: generateId(),
        type: 'CLIENT',
        referenceId: client.id,
        name: `${client.firstName} ${client.lastName}`,
        email: client.email,
        phone: client.phoneNumber,
        notifyEmail: true,
        notifyPush: true, // Clients can receive push notifications
        notifySms: false,
      };
      onParticipantsChange([...selectedParticipants, newParticipant]);
      setSearchTerm('');
      setAddMode('none');
    },
    [selectedParticipants, onParticipantsChange]
  );

  const handleAddInquiry = useCallback(
    (inquiry: Inquiry) => {
      const hasAccount = !!inquiry.userId;
      const newParticipant: Participant = {
        id: generateId(),
        type: 'INQUIRY',
        referenceId: inquiry.id,
        name: `${inquiry.firstName} ${inquiry.lastName}`,
        email: inquiry.email,
        phone: inquiry.phoneNumber,
        notifyEmail: true,
        notifyPush: hasAccount, // Only enable push if inquiry has a linked account
        notifySms: false,
      };
      onParticipantsChange([...selectedParticipants, newParticipant]);
      setSearchTerm('');
      setAddMode('none');
    },
    [selectedParticipants, onParticipantsChange]
  );

  const handleAddGuest = useCallback(() => {
    if (!guestForm.name.trim()) return;

    const newParticipant: Participant = {
      id: generateId(),
      type: 'GUEST',
      name: guestForm.name.trim(),
      email: guestForm.email.trim() || undefined,
      phone: guestForm.phone.trim() || undefined,
      notifyEmail: !!guestForm.email.trim(),
      notifyPush: false, // Guests cannot receive push notifications
      notifySms: false,
    };
    onParticipantsChange([...selectedParticipants, newParticipant]);
    setGuestForm({ name: '', email: '', phone: '' });
    setAddMode('none');
  }, [guestForm, selectedParticipants, onParticipantsChange]);

  const handleRemoveParticipant = useCallback(
    (participantId: string) => {
      onParticipantsChange(selectedParticipants.filter((p) => p.id !== participantId));
    },
    [selectedParticipants, onParticipantsChange]
  );

  const handleCancelAdd = useCallback(() => {
    setAddMode('none');
    setSearchTerm('');
    setGuestForm({ name: '', email: '', phone: '' });
  }, []);

  const canAddMore = selectedParticipants.length < maxParticipants;

  return (
    <div className="space-y-4">
      {/* Selected Participants */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Selected Participants ({selectedParticipants.length})
        </label>

        {selectedParticipants.length === 0 ? (
          <div className="text-sm text-gray-500 italic p-4 border border-dashed border-gray-300 rounded-lg text-center">
            No participants selected. Add at least one participant below.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {selectedParticipants.map((participant) => (
              <ParticipantChip
                key={participant.id}
                participant={participant}
                onRemove={() => handleRemoveParticipant(participant.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add Participant Buttons */}
      {addMode === 'none' && canAddMore && (
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddMode('client')}
            className="gap-1"
          >
            <span>👤</span> Add Client
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddMode('inquiry')}
            className="gap-1"
          >
            <span>📋</span> Add Inquiry
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddMode('guest')}
            className="gap-1"
          >
            <span>🔗</span> Add Guest
          </Button>
        </div>
      )}

      {!canAddMore && (
        <p className="text-sm text-amber-600">
          Maximum of {maxParticipants} participants reached.
        </p>
      )}

      {/* Search Panel for Clients */}
      {addMode === 'client' && (
        <SearchPanel
          title="Add Client"
          searchPlaceholder="Search clients by name or email..."
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onCancel={handleCancelAdd}
          isLoading={clientsLoading}
          isEmpty={filteredClients.length === 0}
          emptyMessage={searchTerm ? 'No clients found' : 'No clients available'}
        >
          {filteredClients.map((client) => (
            <SearchResultItem
              key={client.id}
              icon="👤"
              iconBg="bg-aalaw-red"
              iconColor="text-white"
              name={`${client.firstName} ${client.lastName}`}
              subtitle={client.email || 'No email'}
              initials={`${client.firstName[0]}${client.lastName[0]}`}
              onClick={() => handleAddClient(client)}
            />
          ))}
        </SearchPanel>
      )}

      {/* Search Panel for Inquiries */}
      {addMode === 'inquiry' && (
        <SearchPanel
          title="Add Inquiry"
          searchPlaceholder="Search inquiries by name or email..."
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onCancel={handleCancelAdd}
          isLoading={inquiriesLoading}
          isEmpty={filteredInquiries.length === 0}
          emptyMessage={searchTerm ? 'No inquiries found' : 'No inquiries available'}
        >
          {filteredInquiries.map((inquiry) => (
            <SearchResultItem
              key={inquiry.id}
              icon="📋"
              iconBg="bg-purple-100"
              iconColor="text-purple-600"
              name={`${inquiry.firstName} ${inquiry.lastName}`}
              subtitle={inquiry.email || inquiry.phoneNumber || 'No contact info'}
              initials={`${inquiry.firstName[0]}${inquiry.lastName[0]}`}
              badge={inquiry.userId ? 'Has Account' : undefined}
              onClick={() => handleAddInquiry(inquiry)}
            />
          ))}
        </SearchPanel>
      )}

      {/* Guest Form */}
      {addMode === 'guest' && (
        <GuestForm
          formData={guestForm}
          onFormChange={setGuestForm}
          onSubmit={handleAddGuest}
          onCancel={handleCancelAdd}
        />
      )}
    </div>
  );
}

// Participant Chip Component
interface ParticipantChipProps {
  participant: Participant;
  onRemove: () => void;
}

function ParticipantChip({ participant, onRemove }: ParticipantChipProps) {
  const colors = typeBadgeColors[participant.type];
  const icon = typeIcons[participant.type];

  return (
    <div className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg shadow-sm">
      <span className="text-lg">{icon}</span>
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-900 leading-tight">
          {participant.name}
        </span>
        <span className={`text-xs ${colors.text} leading-tight`}>
          {participant.type.charAt(0) + participant.type.slice(1).toLowerCase()}
        </span>
      </div>
      <button
        type="button"
        onClick={onRemove}
        className="ml-1 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label={`Remove ${participant.name}`}
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

// Search Panel Component
interface SearchPanelProps {
  title: string;
  searchPlaceholder: string;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onCancel: () => void;
  isLoading: boolean;
  isEmpty: boolean;
  emptyMessage: string;
  children: React.ReactNode;
}

function SearchPanel({
  title,
  searchPlaceholder,
  searchTerm,
  onSearchChange,
  onCancel,
  isLoading,
  isEmpty,
  emptyMessage,
  children,
}: SearchPanelProps) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h4 className="font-medium text-gray-900">{title}</h4>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="p-4 border-b border-gray-100">
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
            autoFocus
          />
        </div>
      </div>

      <div className="max-h-60 overflow-y-auto p-2">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-aalaw-red"></div>
          </div>
        ) : isEmpty ? (
          <div className="text-center py-6 text-gray-500 text-sm">{emptyMessage}</div>
        ) : (
          <div className="space-y-1">{children}</div>
        )}
      </div>
    </div>
  );
}

// Search Result Item Component
interface SearchResultItemProps {
  icon: string;
  iconBg: string;
  iconColor: string;
  name: string;
  subtitle: string;
  initials: string;
  badge?: string;
  onClick: () => void;
}

function SearchResultItem({
  iconBg,
  iconColor,
  name,
  subtitle,
  initials,
  badge,
  onClick,
}: SearchResultItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
    >
      <div
        className={`w-9 h-9 rounded-full ${iconBg} flex items-center justify-center ${iconColor} font-semibold text-sm`}
      >
        {initials.toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium text-gray-900 truncate text-sm">{name}</p>
          {badge && (
            <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded">
              {badge}
            </span>
          )}
        </div>
        <p className="text-xs text-gray-500 truncate">{subtitle}</p>
      </div>
      <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    </button>
  );
}

// Guest Form Component
interface GuestFormProps {
  formData: GuestFormData;
  onFormChange: (data: GuestFormData) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

function GuestForm({ formData, onFormChange, onSubmit, onCancel }: GuestFormProps) {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
        <h4 className="font-medium text-gray-900">Add Guest</h4>
        <button
          type="button"
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <Input
            value={formData.name}
            onChange={(e) => onFormChange({ ...formData, name: e.target.value })}
            placeholder="Guest name"
            required
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => onFormChange({ ...formData, email: e.target.value })}
              placeholder="guest@example.com"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => onFormChange({ ...formData, phone: e.target.value })}
              placeholder="+1 (555) 123-4567"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={!formData.name.trim()}>
            Add Guest
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ParticipantSelector;
